import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { useParams } from "react-router";
import {
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import clientService from "api/clientService";
import { IClientFile } from "dto/IClientFile";
import PackageAttachment from "components/PackageAttachment";
import { ItemKind } from "dto/IKindItems";
import { DossierItemType } from "dto/Dossier/DossierItemType";
import { handleCreate } from "utils/uploadFile";
import { getUserId } from "utils/getUserId";
import { useAuth } from "react-oidc-context";
import { getKeys } from "utils/getKeys";
import { readFileFromBase64 } from "utils/readFileFromBase64";
import * as openpgp from "openpgp";
import moment from "moment";
import usePDF from "hooks/usePDF";
import KeysContext from "contexts/KeysContext";
import { IAttachment } from "dto/IAttachment";
import NotFoundScreen from "components/NotFoundScreen";
import { useTranslation } from "react-i18next";

const FilesClient: FC<Props> = ({
  uploadTrigger,
  dismissUploadTrigger,
  onPreview,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { keys, setKeys } = useContext(KeysContext);
  const userId = getUserId(auth.user!);
  const { clientId } = useParams();

  const { blob, create, removeBlob } =
    usePDF();
  const [downloadFileName, setDownloadFileName] = useState("");

  const {
    data: filesData,
    refetch,
    isSuccess,
  } = clientService.useGetClientFiles(Number(clientId ?? 0), true);
  const { mutate: createFile } = clientService.useCreateClientFile();

  const inputFile = useRef<HTMLInputElement | null>(null);

  const handleClickToUpload = () => {
    if (!inputFile.current) return;
    inputFile.current.click();
  };

  const handleChooseFile = async (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    const file = (event.target as any).files[0];
    if (!file) return;

    const myKeys = await getKeys(userId!);

    const masterPublicKey = await readFileFromBase64(
      myKeys.publicBase64Key,
      "application/other"
    );
    const armored = new TextDecoder().decode(masterPublicKey);

    const pk = await openpgp.key.readArmored(armored);

    console.log(myKeys);
    const r = await handleCreate(
      [file],
      null,
      pk,
      true,
      true,
      undefined,
      undefined,
      (fileIndex: number, progress: number) => {
        //setActualProgress(progress);
      }
    );
    createFile(
      {
        ClientId: Number(clientId),
        Uri: r[0].uri,
      },
      {
        onSuccess: () => {
          refetch();
        },
      }
    );
  };

  const onDownload = async (a: IAttachment) => {
    setDownloadFileName(a.FileName);
    create(null, a, ItemKind.CaseFile, true);
  };

  useEffect(() => {
    if (!blob) return;
    console.log("Downloading!");
    const URL = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = URL;
    link.setAttribute("download", downloadFileName);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(URL);
    removeBlob();
  }, [blob]);

  useEffect(() => {
    if (uploadTrigger > 0) {
      dismissUploadTrigger();
      handleClickToUpload();
    }
  });

  useEffect(() => {
    if (filesData?.data) {
      const newKeys = filesData.data.map((x) => x.Key);
      setKeys([...keys, ...newKeys]);
    }
  }, [filesData?.data]);

  return (
    <div className={styles.wrapper}>
      <input
        type="file"
        id="file"
        ref={inputFile}
        style={{ display: "none" }}
        onChange={handleChooseFile}
      />

      {isSuccess && filesData?.data.length === 0 && (
        <div className={styles.empty}>
          <NotFoundScreen
            size="small"
            title={t("emptyStates.noPowersOfAttorney")}
            text={t("emptyStates.addFirst")}
            buttonName={t("clients.addPowerOfAttorney")}
            onClick={() => handleClickToUpload()}
          />
        </div>
      )}

      {filesData?.data &&
        isSuccess &&
        filesData.data.map((c: IClientFile) => (
          <PackageAttachment
            key={c.Id}
            date={moment(c.CreationDate).format("yyyy-MM-DD hh:mm")}
            dateInColumn={true}
            className={styles.attachment}
            attachment={{
              Id: c.Id,
              FileName: c.FileName,
              Size: c.Size,
              Uri: c.Uri,
              ContentType: c.ContentType,
              CreationDate: c.CreationDate,
              CommentsCount: 0,
              DeskInfo_IsOn: false,
              EncryptionKeyId: c.KeyId,
              DossierId: null,
              Format: c.ContentType,
              IsShared: false,
              Keys: [],
              Kind: ItemKind.CaseFile,
              Label: null,
              LastModified: c.CreationDate,
              ObjectKeys: [],
              ShowInDossier: 0,
              TagCount: 0,
              Type: DossierItemType.Attachment,
            }}
            onDownload={(a) => onDownload(a)}
            onPreview={onPreview}
          />
        ))}
    </div>
  );
};

export default FilesClient;
